import { HttpClient }              from '@angular/common/http';
import {
	Injectable,
	OnDestroy
}                                  from '@angular/core';
import { KeycloakServiceWrapper }  from '@app/core/services/authorization/keycloak-service-wrapper';
import { User }                    from '@app/modules/user/models/user.model';
import { LoggerService }           from '@app/shared/services/logger.service';
import { ToasterService }          from '@app/shared/services/toaster.service';
import { AUTH_CONFIG }             from '@appConfig/authorization-config';
import { KEYCLOACK_CONFIG }        from '@appConfig/keycloak-config';
import { CurrentUserStoreActions } from '@core/store/feature/current-user';
import {
	ApplicationScopeTypes,
	ScopePermissionCheckEnum
}                                  from '@models/scope.enums';
import { Store }                   from '@ngrx/store';
import { RoleTypes }               from '@shared/models/roles.model';
import { KeycloakUtils }           from '@utils/helpers';
import {
	KeycloakEvent,
	KeycloakEventType,
	KeycloakService
}                                  from 'keycloak-angular';
import { Subject }                 from 'rxjs';
import { takeUntil }               from 'rxjs/operators';

@Injectable( {
	providedIn: 'root'
} )
export class AuthorizationService extends KeycloakServiceWrapper implements OnDestroy {

	private _ngUnsubscribe: Subject<void> = new Subject();

	protected _keycloakService: KeycloakService;

	constructor(
		private readonly store$: Store,
		keycloakService: KeycloakService,
		private readonly toaster: ToasterService,
		protected readonly logger: LoggerService,
		protected http: HttpClient
	) {
		super();
		this._keycloakService = keycloakService;
		if ( !KEYCLOACK_CONFIG.useKeycloakService ) {
			this.init().catch( error => this.logger.error( error ) );
			this.toaster.warning(
				'You are working without KeycloakService and Backend connection currently!\nApplication runs in MockMode.',
				'No KeycloakService Configuration Mode'
			);
			return;
		}

		if ( keycloakService.isLoggedIn() ) {
			this.init().catch( error => this.logger.error( error ) );
			return;
		}

		this.logger.error( 'authorization service was not initialized.. user not logged in' );
	}

	protected updateCurrentUser(): void {
		this.store$.dispatch(
			new CurrentUserStoreActions.LoginSuccessAction(
				{
					currentUser: {
						...new User(),
						roles: this.userRoles as RoleTypes[]
					},
					currentUserId: this.currentUserId,
					isAuthenticated: this.isLoggedIn,
					managedObject: this.managedObject,
					profile: this.profile,
					roles: this.userRoles,
					tenantId: this.tenantId
				}
			)
		);
	}

	async init() {
		await this.getKeycloakData( true );

		this._keycloakService.keycloakEvents$
		.pipe( takeUntil( this._ngUnsubscribe ) )
		.subscribe( ( keycloakEvent: KeycloakEvent ) => {

			switch ( keycloakEvent.type ) {
				case KeycloakEventType.OnAuthError:
					this.logger.log( 'keycloakEvent AuthError' );
					KeycloakUtils.deeplinkSessionStorageSet();
					this._keycloakService.logout( KeycloakUtils.getRedirectUri( { encodeURI: true } ) );
					break;

				case KeycloakEventType.OnAuthLogout:
					this.logger.log( 'keycloakEvent AuthLogout' );
					KeycloakUtils.deeplinkSessionStorageSet();
					this._keycloakService.logout( KeycloakUtils.getRedirectUri( { encodeURI: true } ) );
					break;

				case KeycloakEventType.OnAuthRefreshError:
					this.logger.log( 'keycloakEvent AuthRefreshError' );
					KeycloakUtils.deeplinkSessionStorageSet();
					// intentionally set NO redirect here, it caused wrong /auth path with badly handled redirect URL
					break;

				case KeycloakEventType.OnAuthRefreshSuccess:
					this.logger.log( 'keycloakEvent AuthRefreshSuccess' );
					this.getKeycloakData( true );
					break;

				case KeycloakEventType.OnAuthSuccess:
					this.getKeycloakData( true );
					this.logger.log( 'keycloakEvent AuthSuccess' );
					break;

				case KeycloakEventType.OnReady:
					this.logger.log( 'keycloakEvent Ready' );
					break;

				case KeycloakEventType.OnTokenExpired:
					this.logger.log( 'keycloakEvent TokenExpired' );
					break;

				case KeycloakEventType.OnActionUpdate:
					this.logger.log( 'keycloakEvent ActionUpdate' );
					break;
			}

			this.logger.debug( 'AuthorizationService username: ', this.username );
		} );
	}

	hasPermissionForRoute( route: string ): boolean {
		route = '/' + route.split( '/' )[ 1 ]; // /dashboard/{idParam} -> /dashboard
		return this.userRoles.some( role => {
			const allowedRoutes = AUTH_CONFIG[ role ] ? AUTH_CONFIG[ role ].allowedRoutes : null;
			if ( allowedRoutes ) {
				return allowedRoutes.find( allowedRoute => allowedRoute === route );
			}
			return false;
		} );
	}

	hasPermission( scope: ApplicationScopeTypes ): boolean {
		return this.userRoles.some( role => {
			const roleScopesList: ApplicationScopeTypes[] = AUTH_CONFIG[ role ] ? AUTH_CONFIG[ role ]?.scopes : null;
			if ( roleScopesList ) {
				return !!roleScopesList.find( roleScope => roleScope === scope );
			}
			return false;
		} );
	}

	hasAllPermissions( scopes: ApplicationScopeTypes | ApplicationScopeTypes[], permissionCheck: ScopePermissionCheckEnum ): boolean {
		if ( !scopes ) { return false; }

		scopes = Array.isArray( scopes )
						 ? scopes
						 : [ scopes ];

		switch ( permissionCheck ) {
			case ScopePermissionCheckEnum.AND:
				return scopes.every( ( scope: ApplicationScopeTypes ) =>
					this.hasPermission( scope )
				);
			case ScopePermissionCheckEnum.OR:
				return scopes.some( ( scope: ApplicationScopeTypes ) =>
					this.hasPermission( scope )
				);
			default:
				return false;
		}
	}

	hasConditionalAllPermissions(
		conditionFlag: boolean,
		trueConditionScopes: ApplicationScopeTypes | ApplicationScopeTypes[],
		falseConditionScopes: ApplicationScopeTypes | ApplicationScopeTypes[],
		permissionCheck: ScopePermissionCheckEnum
	): boolean {
		return this.hasAllPermissions(
			conditionFlag ? trueConditionScopes : falseConditionScopes,
			permissionCheck
		);
	}


	getDefaultRouteByRole( role: string ): string {
		return AUTH_CONFIG[ role ] ? AUTH_CONFIG[ role ].defaultRoute : null;
	}


	getDefaultRoute( role?: string ): string {
		if ( role ) { return this.getDefaultRouteByRole( role ); }
		let result = null;

		for ( const userRole of this.userRoles ) {
			result = this.getDefaultRouteByRole( userRole );
			if ( result ) { break; }
		}

		return result;
	}

	ngOnDestroy() {
		this.logger.trace( 'AuthorizationService:ngOnDestroy' );
		this._ngUnsubscribe.next();
		this._ngUnsubscribe.complete();
	}
}
